import React from 'react';
import { useLocale } from 'next-intl';
import SAR from '@/public/SAR';
import SlideUpAnimation from '../animation/SlideUpAnimation';
import { cn } from '@/src/lib/utils/command';

type SizeOfImages = 'small' | 'medium' | 'large' | 'exLarge' | 'extraSmall';
export const VatPercent: Record<TCountryId, string> = {
  '116': '',
  '17': '10 %',
  '193': '15 %',
};
export type TCurrency = 'SAR' | 'BHD' | 'KWD' | null;
export type TCountryId = 193 | 17 | 116;

type TProps = {
  price: number | string;
  currency?: TCurrency;
  countryId: TCountryId;
  isHaveDiscount?: boolean;
  size: SizeOfImages;
  className?: string;
  animation?: boolean;
  fontWeight?: string;
  isHaveDiscountColor?: boolean;
};

const sizeConfig: Record<SizeOfImages, { imageSize: string; textSize: string }> = {
  extraSmall: {
    imageSize: 'w-[0.70rem] h-[0.70rem] sm:w-[0.75rem] sm:h-[0.75rem]',
    textSize: 'text-[0.75rem] sm:text-[0.875rem]',
  },
  small: {
    imageSize: 'w-[0.85rem] h-[0.85rem] sm:w-[0.9rem] sm:h-[0.9rem]',
    textSize: 'text-[0.875rem] sm:text-[1rem]',
  },
  medium: {
    imageSize: 'w-[0.85rem] h-[0.85rem] sm:w-[1rem] sm:h-[1rem]',
    textSize: 'text-[1rem] sm:text-[1.15rem]',
  },
  large: {
    imageSize: 'w-[1.25rem] h-[1.25rem] sm:w-[1.5rem] sm:h-[1.5rem]',
    textSize: 'text-[1.25rem] sm:text-[1.5rem]',
  },
  exLarge: {
    imageSize: 'w-[1.375rem] h-[1.375rem] sm:w-[1.75rem] sm:h-[1.75rem]',
    textSize: 'text-[1.5rem] sm:text-[1.75rem]',
  },
};

const currencyDisplayNames: Record<Exclude<TCurrency, null>, { ar: string; en: string }> = {
  SAR: { ar: 'ر.س', en: 'SAR' },
  BHD: { ar: 'د.ب', en: 'BHD' },
  KWD: { ar: 'د.ك', en: 'KWD' },
};

const countryToCurrency: Record<TCountryId, string> = {
  193: 'SAR', // Saudi Arabia
  17: 'BHD', // Bahrain
  116: 'KWD', // Kuwait
};

const PriceDisplay = ({
  price,
  currency,
  size,
  isHaveDiscount,
  countryId,
  className,
  animation,
}: TProps) => {
  const currentLang = useLocale() as 'ar' | 'en';

  const displayCurrency = currency || countryToCurrency[countryId];

  return (
    <div className={cn(`flex items-center gap-1 sm:gap-2`, className)}>
      <div
        className={cn(
          'relative flex items-center gap-0.5 sm:gap-1 rtl:flex-row-reverse',
          displayCurrency !== 'SAR' && 'flex-row-reverse',
        )}
      >
        {displayCurrency === 'SAR' ? (
          <SAR className={sizeConfig[size].imageSize} />
        ) : displayCurrency && displayCurrency !== null ? (
          <span className={sizeConfig[size].textSize}>
            {currencyDisplayNames[displayCurrency as Exclude<TCurrency, null>][currentLang]}
          </span>
        ) : null}

        <div className="flex items-center gap-0.5 sm:gap-1" dir="ltr">
          {animation ? (
            <SlideUpAnimation uniqueKey={price}>{price}</SlideUpAnimation>
          ) : (
            <span className={`flex items-center justify-center ${sizeConfig[size].textSize}`}>
              {/* {price && typeof price === 'number' ? formattedPrice : price} */}
              {price && price}
            </span>
          )}
        </div>

        {isHaveDiscount && (
          <div className="absolute top-1/2 left-0 h-px w-full bg-zinc-500 line-through"></div>
        )}
      </div>
    </div>
  );
};

export default PriceDisplay;
